import type { ZoneAccess, ZoneAccessVerdict, ZoneKind, ZoneVerdict } from '../api/types'
import { formatLongDate } from './format'

/**
 * Dutch wording for the environmental-zone verdicts.
 *
 * This is the one section of the report that says what somebody may *do* with the vehicle rather
 * than what it is, which makes it the one section that can send a reader somewhere they are not
 * allowed. Two rules follow from that and both live here rather than in the component.
 *
 * - **"Geldt niet voor dit voertuig" is not "Toegang".** A passenger car is not *permitted* in a
 *   zero-emission zone; the zone is simply not written about it. The API keeps `NotApplicable` and
 *   `Allowed` apart deliberately, and collapsing them here would eventually tell a van driver
 *   something that is only true of cars.
 * - **A date is never reduced to a tick.** `AllowedUntil` is the entire reason this section is
 *   worth having: it is the difference between a van that is fine and a van that is worthless in
 *   four months. The date goes in the verdict itself, not in a footnote.
 */

export const ZONE_ACCESS_TITLE = 'Milieuzones en zero-emissiezones'

interface ZoneCopy {
  title: string
  /** Who the zone is written about, which is what makes a "geldt niet" verdict readable. */
  appliesTo: string
}

const ZONES: Record<ZoneKind, ZoneCopy> = {
  MilieuzoneGreen: {
    title: 'Milieuzone (groen bord)',
    appliesTo: "Geldt voor diesel-personenauto's en -bestelauto's",
  },
  MilieuzoneBlue: {
    title: 'Milieuzone (blauw bord)',
    appliesTo: "Geldt voor diesel-personenauto's en -bestelauto's, met een strengere eis",
  },
  MilieuzonePurple: {
    title: 'Milieuzone (paars bord)',
    appliesTo: 'Geldt voor vrachtwagens en bussen',
  },
  ZeroEmission: {
    title: 'Zero-emissiezone',
    appliesTo: "Geldt voor bestelauto's en vrachtwagens",
  },
}

/**
 * How firmly a verdict reads.
 *
 * `muted` exists so "geldt niet voor dit voertuig" does not borrow the green of a real permission —
 * it is the absence of a rule, not a pass.
 */
export type ZoneTone = 'positive' | 'warning' | 'negative' | 'muted' | 'neutral'

/** One zone, ready to render. */
export interface ZoneRow {
  zone: ZoneKind
  title: string
  appliesTo: string
  verdict: string
  /** A second line, where the verdict alone would leave the reader with the wrong impression. */
  detail?: string
  tone: ZoneTone
}

export function describeZone(verdict: ZoneVerdict): ZoneRow {
  const copy = ZONES[verdict.zone] ?? { title: verdict.zone, appliesTo: '' }

  return { zone: verdict.zone, ...copy, ...describeVerdict(verdict.verdict, verdict.allowedUntil) }
}

function describeVerdict(
  verdict: ZoneAccessVerdict,
  allowedUntil: string | undefined,
): { verdict: string; detail?: string; tone: ZoneTone } {
  switch (verdict) {
    case 'Allowed':
      return { verdict: 'Toegang', tone: 'positive' }

    case 'AllowedUntil':
      // The date is the verdict here, not a qualifier on it. A reader who takes away "toegang" and
      // misses "tot" is the failure this section exists to prevent.
      return {
        verdict: allowedUntil ? `Toegang tot ${formatLongDate(allowedUntil)}` : 'Toegang, tijdelijk',
        detail: 'Daarna mag dit voertuig de zone niet meer in.',
        tone: 'warning',
      }

    case 'NotAllowed':
      return { verdict: 'Geen toegang', tone: 'negative' }

    case 'NotApplicable':
      return {
        verdict: 'Geldt niet voor dit voertuig',
        detail: 'Deze zone stelt geen eisen aan dit soort voertuig.',
        tone: 'muted',
      }

    case 'Unknown':
    default:
      return {
        verdict: 'Niet te bepalen',
        detail: 'Met de gegevens die de RDW publiceert is hier niets met zekerheid over te zeggen.',
        tone: 'neutral',
      }
  }
}

/**
 * The facts the verdicts were drawn from, as short chips.
 *
 * Worth showing rather than hiding in a footnote: every verdict above follows from the fuel and the
 * emission class, so a reader who disagrees with one can see immediately which of the two inputs to
 * doubt. A vehicle with no registered Euro class is exactly the case where that matters.
 */
export function zoneAccessBasis(zoneAccess: ZoneAccess): string[] {
  const basis: string[] = []

  if (zoneAccess.isZeroEmission) {
    basis.push('Geen uitstoot')
  } else if (zoneAccess.isDiesel) {
    basis.push('Diesel')
  } else {
    basis.push('Geen diesel')
  }

  basis.push(
    zoneAccess.euroEmissionClass !== undefined
      ? `Euro ${zoneAccess.euroEmissionClass}`
      : 'Geen emissieklasse geregistreerd',
  )

  return basis
}
